import { defineComponent, ref, onBeforeMount } from 'vue'
import ru from 'yup-locale-ru'
import { host } from '@/const'
import axios from '@/axios'
import * as yup from 'yup'
import { useField, useForm } from 'vee-validate'
import Preloader from '@/components/preloader/preloader.vue'

export default defineComponent({
  props: {
    isCompany: {
      type: Boolean,
      default: false
    },
    deliveryMethod: {
      type: String,
      default: () => ''
    },
    onSendSuccess: {
      type: Function,
      default: (value: boolean) => value
    }
  },
  setup (props) {
    yup.setLocale(ru)

    const preloader = ref(false)

    const recipientSelect: any = ref({})
    const typeRecipients = ref({
      isCompany: [
        {
          value: 'director',
          text: 'Заберет директор',
          document_info: 'Загрузите скан/фото паспорта',
          document_example: '/documents/passport.pdf'
        },
        {
          value: 'employee',
          text: 'Заберет сотрудник организации',
          document_info: 'Загрузите скан/фото доверенности',
          document_example: '/documents/procuration.pdf'
        },
        {
          value: 'transport_company',
          text: 'Заберет транспортная компания',
          document_info: 'Загрузите скан/фото доверительного письма',
          document_example: '/documents/letter.pdf'
        }
      ],
      isIndividual: [
        {
          value: 'myself',
          text: 'Заберу сам',
          document_info: 'Загрузите скан/фото паспорта',
          document_example: '/documents/passport.pdf'
        },
        {
          value: 'another_man',
          text: 'Будет забирать другой человек',
          document_info: 'Загрузите скан/фото доверенности',
          document_example: '/documents/procuration.pdf'
        },
        {
          value: 'transport_company',
          text: 'Заберет транспортная компания',
          document_info: 'Загрузите скан/фото доверительного письма',
          document_example: '/documents/letter.pdf'
        }
      ]
    })

    const FILE_SIZE = 15 * 1000 // 15 мегабайт в байтах
    const uploadFilePercentage = ref(0)
    const SUPPORTED_FORMATS = [
      'image/jpg',
      'image/jpeg',
      'image/png',
      'application/pdf'
    ]

    onBeforeMount(() => {
      if (props.isCompany) {
        recipientSelect.value = typeRecipients.value.isCompany[0]
      } else {
        recipientSelect.value = typeRecipients.value.isIndividual[0]
      }
    })

    function currentRecipes () {
      if (props.isCompany) {
        return typeRecipients.value.isCompany
      } else {
        return typeRecipients.value.isIndividual
      }
    }

    function selectRecipient (recipe: any) {
      recipientSelect.value = recipe
      recipient.value = recipe.value
    }

    function selectFile ($event: any) {
      document.value = $event.target.files[0] as File
      uploadFilePercentage.value = 0
    }

    const schema = yup.object().shape(
      {
        recipient: yup.string().required(),
        document: yup.mixed()
          .test(
            'fileSize',
            'Файл слишком большой',
            value => {
              return value && value.size > FILE_SIZE
            }
          )
          .test(
            'fileFormat',
            'Этот формат не поддерживается',
            value => {
              if (value && SUPPORTED_FORMATS.includes(value.type)) {
                if (uploadFilePercentage.value === 0) {
                  preloader.value = true
                  const formData: FormData = new FormData()
                  formData.append('file', value)
                  formData.append('ownerType', 'clientDocuments')
                  uploadFilePercentage.value = 1
                  // TODO Нужно подставить url куда будут файлы загружаться
                  const endpoint = `${host}/api/v1/`

                  axios.post(endpoint, formData)
                    .then(json => {
                      console.log(json)
                    })
                    .catch(e => {
                      console.log(e)
                      uploadFilePercentage.value = 0
                    })
                    .finally(() => {
                      preloader.value = false
                      uploadFilePercentage.value = 100
                    })
                }
                return true
              } else {
                uploadFilePercentage.value = 0
                preloader.value = false
                return false
              }
            }
          )
          .required()
      }
    )

    type FormInputs = {
      recipient: string
      document: File | string
    }

    const { handleSubmit } = useForm<FormInputs>({ validationSchema: schema })

    const { value: recipient, errorMessage: recipientError } = useField<string | undefined>('recipient')
    const { value: document, errorMessage: documentError } = useField<File | any>('document')

    const onSubmit = handleSubmit(async (values) => {
      preloader.value = true
      console.log('handleSubmit', values)
      const url = '/api/v1/client/delivery-edit'
      const data = {
        isCompany: props.isCompany,
        deliveryMethod: props.deliveryMethod,
        ...values
      }

      try {
        const response = (await axios.post(`${host}${url}`, data))
        console.log(response.data)
        preloader.value = false
        props.onSendSuccess(true)
      } catch (e) {
        preloader.value = false
        props.onSendSuccess(false)
      }
      // TODO дописать запрос на отправку формы
    })

    return () => {
      return (
        <form onSubmit={values => onSubmit(values)} >
          <div class="steps">
            <div class="step">
              <p>1. Кто будет забирать заказ?</p>
              {currentRecipes().map((recipe) => (
                <div class="step-input">
                  <input
                    type="radio"
                    name="recipient"
                    id={recipe.value}
                    value={recipient.value}
                    checked={recipe.value === recipientSelect.value}
                    onClick={() => selectRecipient(recipe) }
                  />
                  <label for={recipe.value}>
                    { recipe.text }
                  </label>
                </div>
              ))}
              <span class="error-message">{recipientError.value}</span>
            </div>
            <div class="step">
              <p>2. { recipientSelect.value.document_info }</p>
              <div class="document-block">
                <div class="document">
                  <input
                    type="file"
                    id="document"
                    name="document"
                    onChange={ ($event) => selectFile($event) }
                  />
                  {preloader.value && uploadFilePercentage.value < 100
                    ? (<Preloader style="position: relative; margin: 30px auto; top: -10px;"/>)
                    : (
                      <label for="document" class="file-upload">
                        Выбрать файл
                      </label>
                    )
                  }
                  {uploadFilePercentage.value > 0
                    ? (
                      <div class="progress-file">
                        <div class="pregress-bar">
                          <div class="custom-progress" style={`width:${uploadFilePercentage.value}%`}/>
                          <progress max="100"/>
                        </div>
                        <span>{uploadFilePercentage.value}%</span>
                      </div>
                    )
                    : null
                  }
                  {document.value && !documentError.value
                    ? (<span class="file-upload-path">{ document.value ? document.value.name : '' }</span>)
                    : null
                  }
                  {documentError.value
                    ? (<span class="error-message">{documentError.value}</span>)
                    : null
                  }
                  <span style={!documentError.value && !document.value ? 'margin-top:30px;' : ''}>JPEG, PNG, PDF - не более 15MB</span>
                </div>
              </div>
              <a href={recipientSelect.value.document_example} target="_blank" class="show-document">
                Посмотреть пример документа
              </a>
            </div>
          </div>
          {preloader.value && uploadFilePercentage.value === 100
            ? (<Preloader style="position: relative; margin: 50px auto; top: -10px;"/>)
            : (<button>Завершить подтверждение</button>)
          }
        </form>
      )
    }
  }
})